import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Modal, message, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { pw9119Api } from '../../services/pw9119Api';
import { usePW9119Mode } from './hooks/usePW9119Mode';
import { usePW9119Record } from './hooks/usePW9119Record';
import { usePW9119Navigation } from './hooks/usePW9119Navigation';
import { usePW9119Lookup } from './hooks/usePW9119Lookup';
import { useModulePermission } from '../../hooks/usePermission';
import { Modules } from '../../constants/permissions';
import PermissionGuard from '../../components/PermissionGuard';
import ActionBar from './components/ActionBar';
import FormFields from './components/FormFields';
import './PW9119Form.css';

const PW9119Form: React.FC = () => {
  const navigate = useNavigate();
  const perms = useModulePermission(Modules.PW9119);

  // Hooks
  const { mode, setMode, isEditing } = usePW9119Mode();
  const { names, resolveSingleName, resolveAllNames, clearNames } = usePW9119Lookup();
  const {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = usePW9119Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (cardNo: string) => {
    const result = await loadRecord(cardNo);
    if (result) {
      resolveAllNames(result);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = usePW9119Navigation(loadRecordWithNames);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ──

  const handleAdd = useCallback(() => {
    if (isEditing) {
      // Save
      handleSave(mode, (result) => {
        applyResult(result);
        setMode('browse');
        loadList().then(ids => {
          const idx = ids.indexOf(result.f919_01);
          if (idx >= 0) setCurrentIdx(idx);
        });
      });
    } else {
      // Start add mode
      clearNames();
      resetForAdd();
      setMode('add');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode, clearNames, resetForAdd, loadList, setCurrentIdx]);

  const handleModify = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
    } else {
      // Start edit mode
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteRecord = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除卡號「${record.f919_01}」的保管記錄？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          const ids = await loadList();
          if (ids.length > 0) {
            const newIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(newIdx);
            await loadRecordWithNames(ids[newIdx]);
          } else {
            setCurrentIdx(-1);
            clearNames();
            clearFieldErrors();
          }
        }
      },
    });
  }, [record, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, clearNames, clearFieldErrors]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) return;
    try {
      const res = await pw9119Api.search(keyword.trim());
      if ((res.data || []).length === 0) {
        message.info('查無資料');
        return;
      }
      const ids = (res.data || []).map(r => r.f919_01);
      setCurrentIdx(0);
      await loadRecordWithNames(ids[0]);
    } catch {
      message.error('查詢失敗');
    }
  }, [setCurrentIdx, loadRecordWithNames]);

  // ── Search state ──
  const [searchText, setSearchText] = React.useState('');

  return (
    <PermissionGuard module={Modules.PW9119} action="query">
      <Spin spinning={loading}>
        <div className="pw9119-form">
          <div className="page-wrapper">

            {/* Title */}
            <div className="page-title-bar">
              <h1>感應卡保管建檔 (pw9119f1)</h1>
              <span className="nav-info">
                {allIds.length > 0
                  ? `${currentIdx + 1} / ${allIds.length}`
                  : '無資料'}
              </span>
            </div>

            {/* Action Bar */}
            <ActionBar
              mode={mode}
              isEditing={isEditing}
              hasRecord={!!record}
              currentIdx={currentIdx}
              allIdsLength={allIds.length}
              permissions={perms}
              onAdd={handleAdd}
              onModify={handleModify}
              onDelete={handleDeleteRecord}
              onNavTo={handleNavTo}
              onSearch={() => handleSearch(searchText)}
              onExit={() => navigate('/erp')}
            />

            {/* Search Bar */}
            <div className="search-bar">
              <Input
                placeholder="輸入卡號或員工編號查詢"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onPressEnter={() => handleSearch(searchText)}
                disabled={isEditing}
                style={{ width: 240 }}
                maxLength={20}
                suffix={<SearchOutlined />}
              />
              <button
                className="btn"
                disabled={isEditing}
                onClick={() => handleSearch(searchText)}
              >
                查詢
              </button>
              <button
                className="btn"
                disabled={isEditing}
                onClick={async () => {
                  setSearchText('');
                  const ids = await loadList();
                  if (ids.length > 0) {
                    setCurrentIdx(0);
                    await loadRecordWithNames(ids[0]);
                  }
                }}
              >
                全部
              </button>
            </div>

            {/* Form Content */}
            <div className="form-content">
              <div className="section-divider">感應卡保管資料</div>
              <FormFields
                formData={formData}
                names={names}
                isEditing={isEditing}
                mode={mode}
                getField={getField}
                setField={setField}
                resolveSingleName={resolveSingleName}
                getFieldError={getFieldError}
              />
            </div>

          </div>
        </div>
      </Spin>
    </PermissionGuard>
  );
};

export default PW9119Form;
